import Box from "@@/elements/Box";
import Container from "@@/elements/Container";
import { Add, Minus } from "@@/elements/Icons/icons";
import Typography from "@@/elements/Typography";
import { ScreenSize, useIsBreakpointOrBelow } from "@@/hooks/device/use-breakpoint";
import OptimizelyBlock, { OptimizelyBlockProps } from "@@/integrations/optimizly/Block";
import EpiProperty from "@@/integrations/optimizly/Property";
import { useState } from "react";
import TextButtonIcon from "../Button/TextButtonIcon";
import RichText from "../RichText";
import { DocumentItem, FileProps } from "./documentItem";
import styles from "./index.module.scss";

export type TwoColumnRTEProps = {
  leftBody?: string;
  leftDocuments?: FileProps[],
  rightBody?: string;
  rightDocuments?: FileProps[],
  backgroundColor: "beige" | "white" | "blue" | "green",
  header?: string;
  showAllDocuments?: boolean,
  showAllText?: string,
  showLessText?: string;
};

const TwoColumnRTE = ({ leftBody, leftDocuments, rightBody, rightDocuments, backgroundColor, header, showAllDocuments, showAllText, showLessText }: TwoColumnRTEProps) => {
  const MAX_VISIBLE_ITEMS: number = 3;
  const isSmall = useIsBreakpointOrBelow(ScreenSize.SMALL);
  const [openDocuments, setOpenDocuments] = useState<boolean>(false);
  const allDocuments = [...(leftDocuments || []), ...(rightDocuments || [])];

  const colorMapper = (color: string) => {
    switch (color) {
      case "beige":
        return "ComplementaryBeigeTint03";
      case "white":
        return "White";
      case "blue":
        return "PrimaryBlueTint03";
      case "green":
        return "PrimaryGreenTint03";
      default:
        return "White";
    }
  };

  return (
    <Container
      size="full"
      pageGutter={0}
      backgroundColor={colorMapper(backgroundColor)}
      className={backgroundColor === "white" ? styles.two_column__white : undefined}
      component="section">
      <Container>
        <EpiProperty<TwoColumnRTEProps> name="header">
          <Box>
            {header &&
              <Box pb={6}>
                <Typography variant="heading3">{header}</Typography>
              </Box>
            }
          </Box>
        </EpiProperty>
        <Box className={styles.grid}>
          <Box>
            {leftBody && (
              <EpiProperty<TwoColumnRTEProps> name="leftBody">
                <Box>
                  <RichText body={leftBody} />
                </Box>
              </EpiProperty>
            )}
            {leftDocuments && leftDocuments.length > 0 && !isSmall && (
              <EpiProperty<TwoColumnRTEProps> name="leftDocuments">
                <Box
                  pt={leftBody && leftDocuments ? 6 : 0}>
                  <ul className={styles.documents_list}>
                    {leftDocuments.map((document, index) => (
                      <DocumentItem
                        key={index}
                        file={document}
                      />
                    ))}
                  </ul>
                </Box>
              </EpiProperty>
            )}

          </Box>
          <Box>
            {rightBody && (
              <EpiProperty<TwoColumnRTEProps> name="rightBody">
                <Box
                pt={isSmall && leftBody ? 6 : 0}>
                  <RichText body={rightBody} />
                </Box>
              </EpiProperty>
            )}
            <Box>
              {rightDocuments && rightDocuments.length > 0 && !isSmall && (
                <EpiProperty<TwoColumnRTEProps> name="rightDocuments">
                  <Box
                    pt={rightBody && rightDocuments ? 6 : 0}>
                    <ul className={styles.documents_list}>
                      {rightDocuments.map((document, index) => (
                        <DocumentItem
                          key={index}
                          file={document}
                        />
                      ))}
                    </ul>
                  </Box>
                </EpiProperty>
              )}

            </Box>
          </Box>
          {isSmall && allDocuments.length > 0 && (
            <Box
              pt={6}>
              <ul className={styles.documents_list}>
                {(showAllDocuments
                  ? allDocuments
                  : allDocuments.slice(0, openDocuments ? allDocuments.length : MAX_VISIBLE_ITEMS)
                ).map((document, index) => (
                  <DocumentItem
                    key={index}
                    file={document}
                  />
                ))}
              </ul>
              {!showAllDocuments && (allDocuments.length > MAX_VISIBLE_ITEMS) && (
                <Box
                  className={styles.button_wrapper}
                  width="full">
                  <TextButtonIcon
                    color="grey"
                    onClick={() => setOpenDocuments(!openDocuments)}
                    label={!openDocuments ? showAllText : showLessText}
                    direction="right">
                    {openDocuments ? <Minus /> : <Add />}
                  </TextButtonIcon>
                </Box>)
              }
            </Box>
          )}
        </Box>


      </Container>
    </Container>
  );
};
export type ProvidedTwoColumnRTEProps =
  OptimizelyBlockProps<TwoColumnRTEProps> & TwoColumnRTEProps;

const ProvidedTwoColumnRTE = ({
  blockId,
  inEditMode,
  properties,
  ...rest
}: ProvidedTwoColumnRTEProps) => (
  <OptimizelyBlock
    blockId={blockId}
    inEditMode={inEditMode}
    properties={properties}
  >
    {(optimizlyProperties) => (
      <TwoColumnRTE
        {...rest}
        {...optimizlyProperties} />
    )}
  </OptimizelyBlock>
);

export default ProvidedTwoColumnRTE;